import { cloneDeep } from "es-toolkit/compat";
import { Grid, IGridItem } from "./Grid";

class DraggingModule {
    constructor(private grid: Grid) {}

    move(itemToSwitch: IGridItem, itemToSwitchWith: IGridItem) {
        const itemToSwitchRow = this.grid.getRow(itemToSwitch.id);
        const itemToSwitchWithRow = this.grid.getRow(itemToSwitchWith.id);

        if (itemToSwitchRow === -1 || itemToSwitchWithRow === -1) {
            console.error("Item to move or item to switch with not found", { itemToSwitch, itemToSwitchWith });
            return;
        }

        const _grid = cloneDeep(this.grid.grid);

        // * If there is not enough space, create a new row above for the item to switch (active Item)
        const { hasSpace: destinationRowHasSpace, colStart: destinationColStart } = this.grid.getRowConsecutiveSpace(
            _grid[itemToSwitchWithRow],
            itemToSwitch.colSpan ?? 1
        );

        const { hasSpace: rightAboveHasSpace, colStart: rightAboveColStart } = this.grid.getRowConsecutiveSpace(
            _grid[itemToSwitchWithRow - 1],
            itemToSwitch.colSpan ?? 1
        );

        // * If the colspans are the same, just switch the items
        if (itemToSwitch.colSpan === itemToSwitchWith.colSpan) {
            console.log("CASE 1", { itemToSwitch, itemToSwitchWith });
            const itemCoordinates = this.grid.getItemCoordinates(itemToSwitch.id);
            const itemWithCoordinates = this.grid.getItemCoordinates(itemToSwitchWith.id);
            this.grid.removeItem(itemCoordinates);
            this.grid.removeItem(itemWithCoordinates);

            this.grid.placeItem(itemToSwitch, itemWithCoordinates, itemWithCoordinates?.col ?? 0);
            this.grid.placeItem(itemToSwitchWith, itemCoordinates, itemCoordinates?.col ?? 0);
            return;
        }

        // * If the items rows are the same, just switch the items
        if (itemToSwitchRow === itemToSwitchWithRow) {
            console.log("CASE 2", { itemToSwitch, itemToSwitchWith });
            this.switchItems({ ...itemToSwitch, row: itemToSwitchRow }, { ...itemToSwitchWith, row: itemToSwitchWithRow });
            return;
        }
        // * If the destination row has space, just switch the items
        if (destinationRowHasSpace) {
            console.log("CASE 3", { itemToSwitch, itemToSwitchWith });
            const switchCoordinates = this.grid.getItemCoordinates(itemToSwitch.id);
            const switchWithCoordinates = this.grid.getItemCoordinates(itemToSwitchWith.id);
            this.grid.removeItem(switchCoordinates);
            this.grid.placeItem(itemToSwitch, switchWithCoordinates, destinationColStart);
            return;
        }
        // * If the item to switch colSpan is different to the item to switch with colSpan
        if (itemToSwitch.colSpan !== itemToSwitchWith.colSpan) {
            const { hasSpace: itemToSwitchWithRowHasSpace, colStart: itemToSwitchWithRowColStart } = this.grid.getRowConsecutiveSpace(
                _grid[itemToSwitchWithRow],
                itemToSwitchWith.colSpan ?? 1
            );
            const { hasSpace: itemToSwitchRowHasSpace, colStart: itemToSwitchRowColStart } = this.grid.getRowConsecutiveSpace(
                _grid[itemToSwitchRow],
                itemToSwitch.colSpan ?? 1
            );

            if (itemToSwitchRowHasSpace && itemToSwitchWithRowHasSpace) {
                console.log("CASE 4", { itemToSwitch, itemToSwitchWith });
                const switchWithCoordinates = this.grid.getItemCoordinates(itemToSwitchWith.id);
                const switchCoordinates = this.grid.getItemCoordinates(itemToSwitch.id);
                if (!switchWithCoordinates || !switchCoordinates) return;
                this.grid.removeItem(switchCoordinates);
                this.grid.removeItem(switchWithCoordinates);
                this.grid.placeItem(itemToSwitch, switchWithCoordinates, switchWithCoordinates.col);
                this.grid.placeItem({ ...itemToSwitchWith, colStart: switchCoordinates.col }, switchCoordinates, switchCoordinates.col);

                return;
            }
        }
        // * If the right above row has space, just switch the items
        if (itemToSwitchRow !== itemToSwitchWithRow && rightAboveHasSpace) {
            console.log("CASE 5", { itemToSwitch, itemToSwitchWith });
            const switchCoordinates = this.grid.getItemCoordinates(itemToSwitch.id);
            this.grid.removeItem(switchCoordinates);
            this.grid.placeItem(
                { ...itemToSwitch, colStart: rightAboveColStart ?? 0 },
                { col: rightAboveColStart ?? 0, row: (itemToSwitchWithRow ?? 0) - 1, colSpan: itemToSwitch.colSpan ?? 1 },
                rightAboveColStart
            );
            return;
        }
        // * If the items rows are different and the destination row hasn't space & the right above row hasn't space, create a new row above the item to switch with
        if (itemToSwitchRow !== itemToSwitchWithRow && !destinationRowHasSpace && !rightAboveHasSpace) {
            console.log("CASE 6", { itemToSwitch, itemToSwitchWith });
            this.grid.removeItem(this.grid.getItemCoordinates(itemToSwitch.id));
            const newRow = this.grid.addRow(itemToSwitchWithRow);
            this.grid.placeItem(itemToSwitch, this.grid.getItemCoordinates(this.grid.grid[newRow][0].id), 0);
            return;
        }

        // * If the item to switch row is different from the item to switch with row, just switch the items
        if (itemToSwitchRow !== itemToSwitchWithRow) {
            console.log("CASE 7", { itemToSwitch, itemToSwitchWith });
            this.grid.replaceItemWithEmptyItems(itemToSwitchRow, itemToSwitch.colSpan ?? 1, itemToSwitch.colStart);
            this.grid.addRow(itemToSwitchWithRow);
            this.grid.placeItem(itemToSwitch, this.grid.getItemCoordinates(itemToSwitchWith.id), 0);
            return;
        }
    }

    private switchItems(itemToSwitch: IGridItem & { row: number }, itemToSwitchWith: IGridItem & { row: number }) {
        const _grid = cloneDeep(this.grid.grid);
        if (itemToSwitch.row === itemToSwitchWith.row) {
            const row = _grid[itemToSwitch.row];
            const itemToSwitchIndex = row.findIndex((item) => item.id === itemToSwitch.id);
            const itemToSwitchWithIndex = row.findIndex((item) => item.id === itemToSwitchWith.id);

            row[itemToSwitchIndex] = { ...itemToSwitchWith, colStart: itemToSwitch.colStart };
            row[itemToSwitchWithIndex] = { ...itemToSwitch, colStart: itemToSwitchWith.colStart };
            this.grid.setGrid(_grid);
            return;
        }

        const virtualRow = Grid.virtualizeRow(_grid[itemToSwitch.row]);
        const virtualRowWith = Grid.virtualizeRow(_grid[itemToSwitchWith.row]);

        for (let i = itemToSwitch.colStart; i < itemToSwitch.colStart + (itemToSwitch.colSpan ?? 1); i++) {
            virtualRow[i] = { ...itemToSwitchWith, colStart: itemToSwitch.colStart };
        }
        for (let i = itemToSwitchWith.colStart; i < itemToSwitchWith.colStart + (itemToSwitchWith.colSpan ?? 1); i++) {
            virtualRowWith[i] = { ...itemToSwitch, colStart: itemToSwitchWith.colStart };
        }
        _grid[itemToSwitch.row] = Grid.devirtualizeRow(virtualRow);
        _grid[itemToSwitchWith.row] = Grid.devirtualizeRow(virtualRowWith);

        this.grid.setGrid(_grid);
        return;
    }
}

export { DraggingModule };
